import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import EventWatermark from '../EventWatermark';
import {
  formatCardDateLong,
  formatTimeRange,
  getStartingPrice,
  getStartingOriginalPrice,
  getEventDisplay,
  hasMultiplePackages,
  formatKesPrice,
  getEventLocationShort,
  getEventShortAbsoluteUrl
} from '../../utils/eventFormat';
import { getEventImageUrl, DEFAULT_EVENT_IMAGE } from '../../utils/mediaUrl';

const EventCard = ({ event }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => getEventImageUrl(event.imageUrl));
  const display = getEventDisplay(event);
  const multiPackage = hasMultiplePackages(event);
  const startingPrice = getStartingPrice(event);
  const startingOriginalPrice = getStartingOriginalPrice(event);
  const timeRange = display.timeRange || formatTimeRange(event.startTime, event.endTime);

  const locationLine = getEventLocationShort(event);

  useEffect(() => {
    setImageSrc(getEventImageUrl(event.imageUrl));
  }, [event.imageUrl]);

  const handleImageError = () => {
    setImageSrc(DEFAULT_EVENT_IMAGE);
  };

  const getDaysLeft = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Past';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day left';
    return `${diffDays} days left`;
  };

  const handleShare = (platform) => {
    const url = getEventShortAbsoluteUrl(event.id);
    const title = event.title;
    const text = `Check out this amazing event: ${title}`;

    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareModal(false);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col border border-gray-100 text-[13px]">
        <EventWatermark status={event.status}>
          <div className="h-full w-full flex flex-col">
            {/* Poster frame 540×675 (4:5) - zoom stays clipped here; card body never moves */}
            <div className="relative w-full aspect-[540/675] bg-gray-200 overflow-hidden shrink-0 isolate">
              {event.status !== 'CANCELLED' ? (
                <Link to={`/events/${event.id}`} className="absolute inset-0 block overflow-hidden group/poster">
                  <img
                    src={imageSrc}
                    alt={event.title}
                    onError={handleImageError}
                    className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none will-change-transform group-hover/poster:scale-105 transition-transform duration-300 ease-out"
                  />
                </Link>
              ) : (
                <img
                  src={imageSrc}
                  alt={event.title}
                  onError={handleImageError}
                  className="absolute inset-0 w-full h-full object-cover object-center opacity-60"
                />
              )}

              {(event.status === 'SOLD_OUT' || event.status === 'CANCELLED') ? (
                <div className="absolute top-2 left-2 z-20">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${
                    event.status === 'SOLD_OUT'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-500 text-white'
                  }`}>
                    {event.status === 'SOLD_OUT' ? 'SOLD OUT' : 'CANCELLED'}
                  </span>
                </div>
              ) : (
                <div className="absolute top-2 left-2 z-20">
                  <span className="px-2 py-0.5 text-[9px] font-semibold rounded-md bg-orange-500 text-white uppercase tracking-wide">
                    {event.categories?.length
                      ? (event.categories.length > 1
                        ? `${event.categories[0].name} +${event.categories.length - 1}`
                        : event.categories[0].name)
                      : (event.category?.name || 'Event')}
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowShareModal(true)}
                className="absolute top-2 right-2 z-20 w-7 h-7 bg-white/95 rounded-md flex items-center justify-center hover:bg-white shadow-sm transition-all"
                aria-label="Share event"
              >
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>

            <div className="p-3 flex-1 flex flex-col min-w-0 relative z-10 bg-white">
              <h3
                className="text-[13px] font-semibold text-gray-900 mb-1.5 truncate leading-snug"
                title={event.title}
              >
                {event.title}
              </h3>

              <div className="space-y-1 mb-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0 text-gray-600">
                  <svg className="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[12px] truncate">
                    {formatCardDateLong(event.date)}
                  </span>
                </div>

                {timeRange && (
                  <div className="flex items-center gap-2 min-w-0 text-slate-500">
                    <svg className="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[12px] truncate">{timeRange}</span>
                  </div>
                )}

                {locationLine && (
                  <div className="flex items-center gap-2 min-w-0 text-gray-500">
                    <svg className="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-[13px] truncate" title={locationLine}>
                      {locationLine}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-auto pt-1">
                <div className="flex justify-between items-end gap-2 mb-2">
                  <div className="min-w-0">
                    {multiPackage && (
                      <p className="text-[10px] text-gray-400 leading-none mb-0.5">Starting from</p>
                    )}
                    <p className="text-[13px] font-bold text-gray-900 leading-tight">
                      {startingOriginalPrice != null && (
                        <span className="block text-[10px] font-medium text-gray-400 line-through leading-none mb-0.5">
                          {formatKesPrice(startingOriginalPrice)}
                        </span>
                      )}
                      {startingPrice === 0 ? 'Free' : formatKesPrice(startingPrice)}
                    </p>
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0">
                    {getDaysLeft(event.date)}
                  </span>
                </div>

                {event.status === 'CANCELLED' ? (
                  <button
                    type="button"
                    disabled
                    className="w-full border-2 border-gray-300 text-gray-400 bg-gray-50 py-2 rounded-lg cursor-not-allowed text-center text-xs font-medium flex items-center justify-center"
                  >
                    Cancelled
                  </button>
                ) : event.status === 'SOLD_OUT' ? (
                  <Link
                    to={`/events/${event.id}`}
                    className="w-full border border-red-500 text-red-500 bg-white py-2 rounded-lg hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors duration-200 font-semibold text-xs text-center flex items-center justify-center"
                  >
                    View Details
                  </Link>
                ) : (
                  <Link
                    to={`/events/${event.id}`}
                    className="w-full border border-orange-500 text-orange-500 bg-white py-2 rounded-lg hover:bg-orange-600 hover:border-orange-600 hover:text-white transition-colors duration-200 font-semibold text-xs text-center flex items-center justify-center"
                  >
                    <svg className="w-3.5 h-3.5 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2Z" />
                      <path d="M13 5v2" />
                      <path d="M13 17v2" />
                      <path d="M13 9v6" />
                    </svg>
                    Get Tickets
                  </Link>
                )}
              </div>
            </div>
          </div>
        </EventWatermark>
      </div>

      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Share Event</h3>
              <button type="button" onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-2">
              {['facebook', 'twitter', 'whatsapp'].map((platform) => (
                <button
                  key={platform}
                  type="button"
                  onClick={() => handleShare(platform)}
                  className="w-full p-3 text-sm text-left rounded-lg border border-gray-200 hover:bg-gray-50 capitalize"
                >
                  Share on {platform}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventCard;
